import { LANGUAGE, type Language } from '@cmoh/shared'
import {
  format as dfFormat,
  getWeek as dfGetWeek,
  isValid as dfIsValid,
  parseISO as dfParseISO,
} from 'date-fns'
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz'

// Tipos
export type IanaTz = string
export type DateOnly = `${number}-${number}-${number}`
export type IsoUtcString = `${string}Z`

// === Date-only ===

/**
 * True if input looks like YYYY-MM-DD
 */
function isDateOnlyString(input: unknown): input is DateOnly {
  return typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input)
}

/**
 * Convert UTC ISO to date-only
 */
function utcIsoToDateOnly(utcIso: string): DateOnly {
  const d = dfParseISO(utcIso)
  if (!dfIsValid(d)) throw new Error(`utcIsoToDateOnly: invalid ISO: ${utcIso}`)
  return formatInTimeZone(d, 'UTC', 'yyyy-MM-dd') as DateOnly
}

/**
 * Convert date-only to local Date for datepickers
 */
function dateOnlyToPickerDate(dateOnly: DateOnly): Date {
  if (!isDateOnlyString(dateOnly))
    throw new Error(`dateOnlyToPickerDate: expected YYYY-MM-DD, got: ${dateOnly}`)
  const [y, m, d] = dateOnly.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/**
 * Extract date-only from any input (Date or string) with a source TZ
 */
function extractDateOnly(input: Date | string, sourceTimezone: IanaTz): DateOnly {
  if (typeof input === 'string') {
    if (isDateOnlyString(input)) return input
    const parsed = dfParseISO(input)
    if (!dfIsValid(parsed)) throw new Error(`extractDateOnly: invalid date string: ${input}`)
    return formatInTimeZone(parsed, sourceTimezone, 'yyyy-MM-dd') as DateOnly
  }
  return formatInTimeZone(input, sourceTimezone, 'yyyy-MM-dd') as DateOnly
}

/**
 * Convert date-only to UTC ISO string for persistence
 */
function dateOnlyToUtcIso(dateOnly: DateOnly): string {
  if (!isDateOnlyString(dateOnly))
    throw new Error(`dateOnlyToUtcIso: expected YYYY-MM-DD, got: ${dateOnly}`)
  return `${dateOnly}T00:00:00Z`
}

/**
 * UI -> API for date-only: reduce a YYYY-MM-DD with userTz and anchor to 00:00Z
 */
function buildApiDateOnly(input: Date | string, userTz: IanaTz): string {
  const dateOnly = extractDateOnly(input, userTz)
  return dateOnlyToUtcIso(dateOnly)
}

/**
 * API/DB -> UI for date-only (without formatting)
 */
function parseApiDateOnly(utcIsoFromDb: string): { dateOnly: DateOnly; pickerDate: Date } {
  const dateOnly = utcIsoToDateOnly(utcIsoFromDb)
  return { dateOnly, pickerDate: dateOnlyToPickerDate(dateOnly) }
}

/**
 * Format date-only by locale (no TZ).
 * Accepts YYYY-MM-DD or ISO UTC; always reduces to date-only and renders with Intl (no shifting day).
 * Optional pattern uses date-fns against the local picker date.
 */
function formatDateOnly(
  dateOnlyOrUtcIso: DateOnly | string,
  locale: Language = LANGUAGE.ES,
  opts?: {
    includeYear?: boolean
    pattern?: string
    intl?: Intl.DateTimeFormatOptions
  },
): string {
  const dateOnly: DateOnly = isDateOnlyString(dateOnlyOrUtcIso)
    ? dateOnlyOrUtcIso
    : utcIsoToDateOnly(dateOnlyOrUtcIso)

  if (opts?.pattern) {
    return dfFormat(dateOnlyToPickerDate(dateOnly), opts.pattern)
  }

  const intlOptions: Intl.DateTimeFormatOptions =
    opts?.intl ??
    ({
      year: opts?.includeYear === false ? undefined : 'numeric',
      month: '2-digit',
      day: '2-digit',
    } as const)

  const formatter = new Intl.DateTimeFormat(locale, { timeZone: 'UTC', ...intlOptions })
  return formatter.format(new Date(`${dateOnly}T00:00:00Z`))
}

// === Instants (with viewer TZ) ===

/**
 * Convert UTC ISO to local Date
 */
function utcToLocal(utcIso: string, viewerTz: IanaTz): Date {
  const d = dfParseISO(utcIso)
  if (!dfIsValid(d)) throw new Error(`fromUTC: invalid ISO: ${utcIso}`)
  return toZonedTime(d, viewerTz)
}

/**
 * Construct UTC ISO from a local date expressed in `sourceTz` wall time.
 *
 * Use when the meeting timezone may differ from the device timezone, or to be DST-safe.
 * If the meeting is in the device timezone and you created the Date with numeric parts,
 * `new Date(y, m-1, d, hh, mm).toISOString()` is fine.
 */
function localToUtc(localDate: Date, sourceTz: IanaTz, timeHHmm?: string): string {
  const d = new Date(localDate)
  if (timeHHmm) {
    const [hh, mm] = timeHHmm.split(':').map(Number)
    d.setHours(hh, mm ?? 0, 0, 0)
  }
  return fromZonedTime(d, sourceTz).toISOString() as IsoUtcString
}

/**
 * Format an instant for display in a target TZ
 */
function formatInstantForDisplay(
  input: Date | string,
  targetTz: IanaTz,
  formatStr = 'dd/MM/yyyy HH:mm',
): string {
  const d = typeof input === 'string' ? dfParseISO(input) : input
  if (!dfIsValid(d)) return ''
  return formatInTimeZone(d, targetTz, formatStr)
}

/**
 * Format an instant for display using Intl (locale-aware) in a given TZ.
 * - Accepts Date or ISO UTC.
 * - By default shows date+time short; you can pass your options.
 */
function formatInstantIntl(
  input: Date | string,
  targetTz: IanaTz,
  locale: Language = LANGUAGE.ES,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  },
): string {
  const d = typeof input === 'string' ? dfParseISO(input) : input
  if (!dfIsValid(d)) return ''
  return new Intl.DateTimeFormat(locale, { timeZone: targetTz, ...options }).format(d)
}

// === General ===

/**
 * Get current date in a given timezone
 */
function nowInTimezone(tz: IanaTz): Date {
  return toZonedTime(new Date(), tz)
}

/**
 * Check if a date is today in a given timezone
 */
function isToday(input: Date | string, tz: IanaTz): boolean {
  const d = typeof input === 'string' ? dfParseISO(input) : input
  if (!dfIsValid(d)) return false
  const today = nowInTimezone(tz)
  const u = toZonedTime(d, tz)
  return dfFormat(today, 'yyyy-MM-dd') === dfFormat(u, 'yyyy-MM-dd')
}

/**
 * Get the week number of a given date
 */
function getWeekNumber(date: Date = new Date()): number {
  return dfGetWeek(date, { weekStartsOn: 1 })
}

/**
 * Get the display name of a given timezone
 */
function getTimezoneDisplayName(tz: IanaTz, locale: Language = LANGUAGE.ES): string {
  try {
    const fmt = new Intl.DateTimeFormat(locale, { timeZone: tz, timeZoneName: 'long' })
    return fmt.formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value ?? tz
  } catch {
    return tz
  }
}

/**
 * Get the offset of a given timezone
 */
function getTimezoneOffset(tz: IanaTz): string {
  try {
    return formatInTimeZone(new Date(), tz, 'OOOO')
  } catch {
    return 'UTC'
  }
}

/**
 * Get difference between two timezones, as minutes or formatted string.
 *
 * Example: getTzDiff("America/Santiago", "Europe/Madrid")
 *   -> "UTC+05" (Madrid está 5h adelante de Santiago ahora)
 */
function getTzDiff(tzA: IanaTz, tzB: IanaTz): string {
  try {
    const now = new Date()
    const a = toZonedTime(now, tzA)
    const b = toZonedTime(now, tzB)
    const diffMs = a.getTime() - b.getTime()
    const totalMin = Math.round(diffMs / 60000)
    const sign = totalMin >= 0 ? '+' : '-'
    const absMin = Math.abs(totalMin)
    const hh = String(Math.trunc(absMin / 60)).padStart(2, '0')
    const mm = String(absMin % 60).padStart(2, '0')
    return mm === '00' ? `${sign}${hh}` : `${sign}${hh}:${mm}`
  } catch {
    return 'UTC'
  }
}

export interface RelativeTimeTranslations {
  justNow: string
  minutesAgo: string
  hoursAgo: string
  daysAgo: string
}

/**
 * Format a date as relative time (e.g., "5 minutes ago", "2 hours ago").
 * Requires a translate function and translation keys.
 */
function formatRelativeTime(
  dateString: string,
  translations: RelativeTimeTranslations,
  formatters?: {
    minutes?: (n: number) => string
    hours?: (n: number) => string
    days?: (n: number) => string
  },
): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return translations.justNow
  if (diffMins < 60) {
    return formatters?.minutes
      ? formatters.minutes(diffMins)
      : translations.minutesAgo.replace('{minutes}', String(diffMins))
  }
  if (diffHours < 24) {
    return formatters?.hours
      ? formatters.hours(diffHours)
      : translations.hoursAgo.replace('{hours}', String(diffHours))
  }
  if (diffDays < 30) {
    return formatters?.days
      ? formatters.days(diffDays)
      : translations.daysAgo.replace('{days}', String(diffDays))
  }
  return date.toLocaleDateString()
}

export const DateUtils = {
  // Date-only
  isDateOnlyString,
  utcIsoToDateOnly,
  dateOnlyToPickerDate,
  extractDateOnly,
  dateOnlyToUtcIso,
  buildApiDateOnly,
  parseApiDateOnly,
  formatDateOnly,

  // Instants
  utcToLocal,
  localToUtc,
  formatInstantForDisplay,
  formatInstantIntl,

  // General
  nowInTimezone,
  isToday,
  getWeekNumber,
  getTimezoneDisplayName,
  getTimezoneOffset,
  getTzDiff,

  // Relative time
  formatRelativeTime,
}
